import {
  Box,
  Button,
  ButtonGroup,
  Portal,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
} from '@chakra-ui/react';
import { FocusLock } from '@chakra-ui/react';
import { TextArea } from '@gamma/ui/form-fields';
import CloseSharpIcon from '@gamma/ui/icons/CloseSharpIcon';
import { ReactNode, useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FeedbackEvent } from '@investigator/shared/types';
import {
  type FeedbackCopy,
  type FeedbackType,
} from '@investigator/shared/types';

/**
 * Props for the FeedbackPopoverForm component
 */
export interface FeedbackPopoverFormProps {
  children: ReactNode;
  /** The type of feedback being collected ('positive' or 'negative') */
  type: FeedbackType;
  /** Localized copy for the form (title, description, options, labels, etc.) */
  copy: FeedbackCopy;
  /** The tracking event to fire when feedback is submitted */
  tracking: FeedbackEvent;
  /** Optional additional context to include in the tracking event */
  trackingContext?: Record<string, unknown>;
  /** Optional callback when popover opens/closes */
  onOpenChange?: (isOpen: boolean) => void;
  /** Optional controlled open state */
  isOpen?: boolean;
}

/**
 * Internal form data structure
 */
interface FeedbackFormData {
  selectedOption: string;
  feedbackText: string;
}

/**
 * A popover form for collecting structured user feedback with optional radio options
 * and a text area. Submits feedback for analytics.
 *
 * Features:
 * - Optional radio button group for categorizing feedback
 * - Optional text area for additional comments
 * - Form validation (radio selection required if radioOptions provided)
 * - Automatic form reset on close
 * - Analytics integration (pendo.js) with custom tracking context
 * - Toast notification if tracking is unavailable
 * - Keyboard accessible with focus trapping and visible focus indicators
 *
 * @example
 * ```tsx
 * <FeedbackPopoverForm
 *   type="positive"
 *   copy={positiveCopy}
 *   tracking={FEEDBACK_EVENTS.AI_ANALYSIS_FEEDBACK}
 *   trackingContext={{ feature: 'agentic-triage', entityType: 'ip' }}
 * >
 *   <IconButton icon={<ThumbUpIcon />} />
 * </FeedbackPopoverForm>
 * ```
 */
export const FeedbackPopoverForm = ({
  type,
  copy,
  tracking,
  trackingContext,
  onOpenChange,
  isOpen,
  children,
}: FeedbackPopoverFormProps): JSX.Element => {
  const disclosure = useDisclosure({
    onOpen: () => {
      reset(
        { selectedOption: '', feedbackText: '' },
        { keepErrors: false, keepDirty: false, keepIsSubmitted: false },
      );
      onOpenChange?.(true);
    },
    onClose: () => onOpenChange?.(false),
  });
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Create unique form ID to prevent conflicts when both forms are mounted
  const formId = `feedback-form-${type}`;

  const { control, handleSubmit, reset, watch } = useForm<FeedbackFormData>({
    defaultValues: {
      selectedOption: '',
      feedbackText: '',
    },
  });

  // Watch the selectedOption to enable/disable submit button
  const selectedOption = watch('selectedOption');
  const disableSubmit = copy.radioOptions ? !selectedOption : false;

  const handleClose = useCallback(() => {
    reset(
      { selectedOption: '', feedbackText: '' },
      { keepErrors: false, keepDirty: false, keepIsSubmitted: false },
    );
    setIsSubmitting(false);
    disclosure.onClose();
  }, [reset, disclosure]);

  const handleDismiss = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const onSubmit = (data: FeedbackFormData) => {
    setIsSubmitting(true);

    const pendoTrack =
      typeof window !== 'undefined' &&
      window.pendo &&
      typeof window.pendo.track === 'function';

    // Send feedback to Pendo
    if (pendoTrack) {
      window.pendo.track(tracking, {
        feedbackType: type,
        selectedOption: data.selectedOption,
        feedbackText: data.feedbackText,
        timestamp: Date.now(),
        ...trackingContext,
      });

      // Show success toast
      toast({
        status: 'success',
        title: copy.successToastTitle,
        description: copy.successMessage,
        isClosable: true,
        position: 'bottom-right',
        duration: 3000,
      });
    } else {
      // Show error toast when Pendo is unavailable
      toast({
        status: 'error',
        title: copy.errorToastTitle,
        description: copy.errorMessage,
        isClosable: true,
        position: 'bottom-right',
      });
    }

    // Close popover after a brief delay to prevent validation issues
    setTimeout(() => {
      handleClose();
    }, 100);
  };

  return (
    <Popover
      isOpen={typeof isOpen === 'boolean' ? isOpen : disclosure.isOpen}
      onOpen={disclosure.onOpen}
      onClose={handleClose} // Always reset form on any popover close
      placement="bottom-end"
      closeOnBlur={false}
      closeOnEsc={true}
      isLazy
      lazyBehavior="unmount"
      returnFocusOnClose={true}
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <Portal>
        <PopoverContent
          w="360px"
          maxH="90vh"
          overflowY="auto"
          data-testid="feedback-popover"
        >
          <FocusLock restoreFocus persistentFocus={false}>
            <PopoverCloseButton top={2} right={2}>
              <CloseSharpIcon size="sm" />
            </PopoverCloseButton>
            <PopoverHeader borderBottom="none">
              <Text textStyle="body-md-bold" data-testid="feedback-title">
                {copy.title}
              </Text>
            </PopoverHeader>
            <PopoverBody>
              <form id={formId} key={formId} onSubmit={handleSubmit(onSubmit)}>
                <VStack spacing={4} align="stretch">
                  <Text
                    textStyle="body-md"
                    data-testid="feedback-description"
                    id={`${formId}-description`}
                  >
                    {copy.description}
                  </Text>
                  {copy.radioOptions && (
                    <Controller
                      name="selectedOption"
                      control={control}
                      rules={{ required: true }}
                      render={({ field, fieldState }) => (
                        <Box
                          p={3}
                          borderRadius="md"
                          borderWidth="1px"
                          borderColor={
                            fieldState.error ? 'red.500' : 'gray.600'
                          }
                          data-testid="feedback-radio-container"
                          _focusWithin={{
                            borderColor: 'blue.500',
                            boxShadow:
                              '0 0 0 1px var(--chakra-colors-blue-500)',
                          }}
                        >
                          <RadioGroup
                            {...field}
                            aria-label="Select feedback category"
                            aria-required={true}
                            aria-describedby={`${formId}-description`}
                          >
                            <Stack spacing={2}>
                              {copy.radioOptions?.map((option) => (
                                <Radio
                                  key={option.value}
                                  value={option.value}
                                  data-testid={`feedback-option-${option.value}`}
                                  _focusVisible={{
                                    outline: '3px solid',
                                    outlineColor: 'blue.400',
                                    outlineOffset: '2px',
                                  }}
                                >
                                  <Text textStyle="body-md">
                                    {option.label}
                                  </Text>
                                </Radio>
                              ))}
                            </Stack>
                          </RadioGroup>
                        </Box>
                      )}
                    />
                  )}
                  <Controller
                    name="feedbackText"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextArea
                        {...field}
                        name="feedbackText"
                        label={copy.textareaLabel}
                        placeholder={copy.textareaPlaceholder}
                        rows={3}
                        data-testid="feedback-textarea"
                        isLabelHidden={false}
                        error={fieldState.error?.message}
                      />
                    )}
                  />
                </VStack>
              </form>
            </PopoverBody>
            <PopoverFooter
              display="flex"
              justifyContent="flex-end"
              borderTop="none"
            >
              <ButtonGroup spacing={2}>
                <Button
                  variant="solid"
                  colorScheme="gray"
                  onClick={handleDismiss}
                  data-testid="feedback-dismiss-button"
                >
                  {copy.dismissButton}
                </Button>
                <Button
                  type="submit"
                  form={formId}
                  variant="solid"
                  colorScheme="blue"
                  isLoading={isSubmitting}
                  isDisabled={disableSubmit}
                  data-testid="feedback-submit-button"
                >
                  {copy.submitButton}
                </Button>
              </ButtonGroup>
            </PopoverFooter>
          </FocusLock>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
